'use client';
import React, { useState, useEffect } from 'react';
import LeadCaptureModal from './LeadCaptureModal';

const bannerSlides = [
  {
    id: 1,
    bg: 'from-orange-600 to-orange-800',
    title: 'Personal Loans',
    subtitle: 'Starting @ 9.98% p.a.',
    desc: 'Get instant approval with minimal documentation',
    cta: 'Apply Now',
    badge: 'Cashback Offer',
    badgeColor: 'bg-green-500',
    emoji: '💰',
  },
  {
    id: 2,
    bg: 'from-orange-500 to-amber-700',
    title: 'Home Loans',
    subtitle: 'Best Rates @ 7.10% p.a.',
    desc: 'Make your dream home a reality today',
    cta: 'Check Eligibility',
    badge: 'Best Rates',
    badgeColor: 'bg-yellow-500',
    emoji: '🏠',
  },
  {
    id: 3,
    bg: 'from-gray-800 to-gray-900',
    title: 'Business Loans',
    subtitle: 'Up to ₹50 Lakhs',
    desc: 'Fuel your business growth with quick funding',
    cta: 'Get Funded',
    badge: 'Quick Disbursal',
    badgeColor: 'bg-orange-500',
    emoji: '📈',
  },
  {
    id: 4,
    bg: 'from-orange-700 to-red-800',
    title: 'Credit Cards',
    subtitle: 'Best Offers & Cashback',
    desc: 'Compare top credit cards and apply instantly',
    cta: 'Compare Cards',
    badge: 'Top Offers',
    badgeColor: 'bg-green-600',
    emoji: '💳',
  },
];

// Floating tabs config
const floatingTabs = [
  { label: 'Home Loan', rate: '7.10%', icon: '🏠', color: 'bg-orange-500', service: 'Home Loan' },
  { label: 'Personal Loan', rate: '9.98%', icon: '💰', color: 'bg-blue-500', service: 'Personal Loan' },
  { label: 'Credit Card', rate: 'Best Offers', icon: '💳', color: 'bg-purple-500', service: 'Credit Card' },
  { label: 'Business Loan', rate: '11.00%', icon: '🏢', color: 'bg-green-500', service: 'Business Loan' },
];

export default function HeroSection() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % bannerSlides?.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const slide = bannerSlides?.[activeSlide];

  const handleTabClick = (service: string) => {
    setSelectedService(service);
    setModalOpen(true);
  };

  return (
    <>
      <LeadCaptureModal isOpen={modalOpen} onClose={() => setModalOpen(false)} service={selectedService} />
      <section className="bg-gray-50 py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            {/* Left content */}
            <div className="flex-1 lg:max-w-[45%]">
              <h1 className="text-3xl lg:text-4xl text-gray-600 mb-4 leading-tight" style={{ fontFamily: 'Roboto Serif, serif' }}>
                India&apos;s best platform for{' '}
                <span className="font-bold text-gray-900 block mt-1">Loans, Cards &amp; Investments</span>
              </h1>
              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">One Stop for all</div>
                    <div className="text-sm font-semibold text-gray-900">Financial Solutions</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Quick, easy &amp;</div>
                    <div className="text-sm font-semibold text-gray-900">hassle free</div>
                  </div>
                </div>
              </div>

              {/* Floating rate tabs */}
              <div className="mt-6 grid grid-cols-2 gap-3">
                {floatingTabs.map((tab) => (
                  <button
                    key={tab.label}
                    onClick={() => handleTabClick(tab.service)}
                    className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3 hover:border-orange-300 hover:shadow-md transition-all duration-200 text-left group"
                  >
                    <div className={`w-9 h-9 ${tab.color} rounded-lg flex items-center justify-center text-lg flex-shrink-0`}>
                      {tab.icon}
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-800">{tab.label}</div>
                      <div className="text-xs text-primary font-bold">{tab.rate}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Right banner slider */}
            <div className="flex-1 w-full lg:max-w-[55%]">
              <div className="relative rounded-xl overflow-hidden" style={{ minHeight: '220px' }}>
                <div className={`bg-gradient-to-r ${slide?.bg} rounded-xl p-6 lg:p-8 text-white transition-all duration-500`} style={{ minHeight: '220px' }}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <span className={`inline-block px-2 py-0.5 text-xs font-semibold text-white rounded ${slide?.badgeColor} mb-3`}>
                        {slide?.badge}
                      </span>
                      <h2 className="text-2xl lg:text-3xl font-bold mb-1">{slide?.title}</h2>
                      <p className="text-lg font-semibold opacity-90 mb-2">{slide?.subtitle}</p>
                      <p className="text-sm opacity-80 mb-4">{slide?.desc}</p>
                      <button
                        onClick={() => handleTabClick(slide?.title || '')}
                        className="bg-white text-primary font-semibold px-5 py-2 rounded-lg text-sm hover:bg-orange-50 transition-colors"
                      >
                        {slide?.cta} &rarr;
                      </button>
                    </div>
                    <div className="text-6xl ml-4 opacity-80">{slide?.emoji}</div>
                  </div>
                </div>

                {/* Dots */}
                <div className="flex justify-center gap-2 mt-3">
                  {bannerSlides?.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveSlide(idx)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        idx === activeSlide ? 'bg-primary w-5' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky floating banner - Home Loan Best Rate */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className="text-white text-lg">🏠</span>
            <span className="text-white font-bold text-sm">Home Loan Best Rates at 7.10%</span>
            <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">LOWEST RATE</span>
          </div>
          <button
            onClick={() => handleTabClick('Home Loan')}
            className="bg-white text-orange-600 font-bold text-xs px-4 py-1.5 rounded-full hover:bg-orange-50 transition-colors"
          >
            Apply Now →
          </button>
        </div>
      </div>
    </>
  );
}
